import { HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import {
    Observable,
    ReplaySubject,
    catchError,
    distinctUntilChanged,
    interval,
    map,
    mergeMap,
    of,
    share,
    startWith,
    timer
} from 'rxjs';

import { ConfigurationService } from '../configuration/configuration.service';
import { SKIP_JAP_HEADER_CONTEXT } from '../endpoint.consts';
import { Logger } from '../logging/logger.service';

/**
 * should be exported only for public (b2c) onprem SPAs, because the url in that particular case is /jap-public/featuretoggle-evaluator/rest
 */
export const FEATURE_TOGGLE_ENDPOINT = new InjectionToken<string>('Endpoint to get toggles', {
    factory: () => {
        return inject(ConfigurationService).getSync('isCloudApp')
            ? DEFAULT_FEATURE_TOGGLE_ENDPOINT
            : DEFAULT_ONPREM_FEATURE_TOGGLE_ENDPOINT;
    }
});
const DEFAULT_FEATURE_TOGGLE_ENDPOINT = '/fto/featuretoggle-evaluator';
const DEFAULT_ONPREM_FEATURE_TOGGLE_ENDPOINT = '/jap/featuretoggle-evaluator/rest';
const CACHE_REFRESH_TIME = 30_000;

@Injectable({
    providedIn: 'root'
})
export class FeatureToggleService {
    #toggleCache = new Map<string, Observable<string>>();
    #http = inject(HttpClient);
    #logger = inject(Logger);
    #featureToggleEndpoint = inject(FEATURE_TOGGLE_ENDPOINT);

    getTreatment$(splitName: string, attributes?: Attributes): Observable<string> {
        return this.#getTreatmentCache$(splitName, attributes);
    }

    getTreatmentForKey$(splitName: string, key: string): Observable<string> {
        return this.#getTreatmentCache$(splitName, undefined, key);
    }

    getTreatmentPeriodically$(splitName: string, periodInMin: number, attributes?: Attributes): Observable<string> {
        // it's in zone, but at most once per minute, so that's OK
        return interval(Math.max(1, periodInMin) * 60 * 1000).pipe(
            startWith('init'),
            mergeMap(() => this.getTreatment$(splitName, attributes)),
            distinctUntilChanged()
        );
    }

    #getTreatmentCache$(splitName: string, attributes?: Attributes, key?: string): Observable<string> {
        const toggleKey = [splitName, this.#hash(attributes), key].filter(Boolean).join('-');

        if (!this.#toggleCache.has(toggleKey)) {
            this.#toggleCache.set(toggleKey, this.#fetchTreatment(splitName, attributes, key));
        }
        return this.#toggleCache.get(toggleKey) as Observable<string>;
    }

    #hash(attributes: Attributes | undefined): string | undefined {
        if (!attributes) {
            return undefined;
        }
        return Object.keys(attributes)
            .sort()
            .reduce((acc, key) => [...acc, key, this.#getSortedContent(attributes[key])], [] as string[])
            .join('-');
    }

    #getSortedContent(value: AttributeValue): string {
        return Array.isArray(value) ? value.sort().join('-') : (value as string);
    }

    #fetchTreatment(splitName: string, attributes?: Attributes, key?: string): Observable<string> {
        const url = `${this.#featureToggleEndpoint}/treatments`;
        let request: Observable<Treatment>;
        if (key) {
            request = this.#http.get<Treatment>(url, {
                params: { splitName, key },
                context: SKIP_JAP_HEADER_CONTEXT.context
            });
        } else if (!attributes) {
            request = this.#http.get<Treatment>(`${url}/${splitName}`, SKIP_JAP_HEADER_CONTEXT);
        } else {
            request = this.#http.post<Treatment>(`${url}/${splitName}`, attributes, SKIP_JAP_HEADER_CONTEXT);
        }
        return request.pipe(
            map((treatment: Treatment) => treatment.treatment),
            share({
                connector: () => new ReplaySubject(1),
                resetOnComplete: () => timer(CACHE_REFRESH_TIME),
                resetOnRefCountZero: false
            }),
            catchError(error => {
                this.#logger.warn(`exception on calling ${url}: ${JSON.stringify(error)}`);
                return of('control');
            })
        );
    }
}

interface Treatment {
    splitName: string;
    treatment: string;
}

export type Attributes = Record<string, AttributeValue>;

export type AttributeValue = string | number | boolean | (string | number)[];
